/**
 * Pure presentation helpers for the School Activity dialog and the locale
 * card's owner line. No React, no API: every function takes the row the
 * server sent (ev_web_v2 /api/activity/list, activity_log_service) and the
 * Localization strings object, and returns text.
 *
 * Kept out of the dialog so the rules that decide what an admin READS —
 * which changed fields are named, how a map-area edit collapses, what a
 * change-owner row says once one of the accounts is renamed — can be tested
 * without mounting anything.
 */

export const ACTIONS = ['create', 'update', 'delete', 'change_owner', 'login'];

const ACTION_KEY = {
  create: 'ACTIVITY_ACTION_CREATE',
  update: 'ACTIVITY_ACTION_UPDATE',
  delete: 'ACTIVITY_ACTION_DELETE',
  change_owner: 'ACTIVITY_ACTION_CHANGE_OWNER',
  login: 'ACTIVITY_ACTION_LOGIN',
};

const ACTION_COLOR = {
  create: 'green',
  update: 'blue',
  delete: 'red',
  change_owner: 'grape',
  login: 'gray',
};

// The six bbox/centre columns move together whenever the map is re-framed;
// six "22.3 -> 22.31" lines say nothing an admin wants. One entry, no values.
const MAP_AREA_FIELDS = ['ul_lat', 'ul_lon', 'br_lat', 'br_lon', 'mid_lat', 'mid_lon'];

const MAX_VALUE_CHARS = 40;

function str(strings, key, fallback) {
  const v = strings && strings[key];
  return (typeof v === 'string' && v) ? v : fallback;
}

function fmt(template, ...args) {
  return String(template).replace(/\{(\d+)\}/g, (m, i) => (args[i] === undefined ? m : String(args[i])));
}

export function actionLabel(strings, action) {
  return str(strings, ACTION_KEY[action], action || '');
}

export function actionColor(action) {
  return ACTION_COLOR[action] || 'gray';
}

export function roleLabel(strings, role) {
  const n = parseInt(role, 10);
  if (isNaN(n)) {
    return '';
  }
  return str(strings, 'ACTIVITY_ROLE_' + n, String(n));
}

export function surfaceLabel(strings, surface) {
  return str(strings, 'ACTIVITY_SURFACE_' + String(surface || '').toUpperCase(), surface || '');
}

export function methodLabel(strings, method) {
  return str(strings, 'ACTIVITY_METHOD_' + String(method || '').toUpperCase(), method || '');
}

export function fieldLabel(strings, field) {
  return str(strings, 'ACTIVITY_FIELD_' + String(field || '').toUpperCase(), field || '');
}

/** display_name, else username, else "#id". An anonymous device has no user
 *  row: the sign-in list shows entity_name (the app display name, or the udid
 *  when the mint had no name) and falls back to the udid in the login detail. */
export function actorName(row) {
  if (!row) {
    return '';
  }
  const named = row.actor_display_name || row.actor_username;
  if (named) {
    return named;
  }
  if (row.actor_user_id) {
    return '#' + row.actor_user_id;
  }
  if (row.action === 'login' && row.entity_name) {
    return String(row.entity_name);
  }
  const detail = row.detail && typeof row.detail === 'object' ? row.detail : null;
  if (detail && detail.udid) {
    return String(detail.udid);
  }
  return '';
}

/**
 * "YYYY-MM-DD HH:mm" from what the API sends for a DATETIME — an ISO string
 * ("2026-09-17T10:05:31") or the MySQL form with a space. STRING SURGERY, not
 * `new Date()`: the column is Hong Kong wall-clock time with no zone marker,
 * and a Date round-trip would shift it by the viewer's offset. Blank for
 * nothing / the MySQL zero date.
 */
export function formatActivityTime(value) {
  if (!value) {
    return '';
  }
  const text = String(value).trim();
  if (!text || text.startsWith('0000-00-00')) {
    return '';
  }
  const m = text.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (m) {
    return m[1] + ' ' + m[2];
  }
  return text;
}

function shortValue(v) {
  if (v === null || v === undefined) {
    return '';
  }
  const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
  return s.length > MAX_VALUE_CHARS ? s.slice(0, MAX_VALUE_CHARS - 1) + '…' : s;
}

/**
 * The `changes` of an update row as display items, in the order the server
 * sent them, with the map-area sextet collapsed into one valueless entry.
 * Returns [] for anything that is not an update with changes.
 */
export function describeChanges(detail, strings) {
  const changes = detail && detail.changes && typeof detail.changes === 'object' ? detail.changes : null;
  if (!changes) {
    return [];
  }
  const out = [];
  let mapArea = false;
  Object.keys(changes).forEach((field) => {
    if (MAP_AREA_FIELDS.indexOf(field) >= 0) {
      mapArea = true;
      return;
    }
    const c = changes[field] || {};
    out.push({
      field,
      label: fieldLabel(strings, field),
      from: shortValue(c.from),
      to: shortValue(c.to),
      hasFrom: Object.prototype.hasOwnProperty.call(c, 'from'),
    });
  });
  if (mapArea) {
    out.push({ field: 'map_area', label: fieldLabel(strings, 'map_area'), from: '', to: '', hasFrom: false });
  }
  return out;
}

/** One line of text for the Details column, whatever the action. */
export function describeDetail(row, strings) {
  if (!row) {
    return '';
  }
  const d = row.detail && typeof row.detail === 'object' ? row.detail : {};
  switch (row.action) {
    case 'update':
      return describeChanges(d, strings)
        .map((c) => (c.from === '' && c.to === '' ? c.label
          : (c.hasFrom ? `${c.label}: ${c.from} → ${c.to}` : `${c.label}: ${c.to}`)))
        .join('; ');
    case 'change_owner': {
      const from = d.from_display_name || d.from_username || (d.from_user_id ? '#' + d.from_user_id : '');
      const to = d.to_display_name || d.to_username || (d.to_user_id ? '#' + d.to_user_id : '');
      return `${from} → ${to}`;
    }
    case 'create':
      return d.source_epack_id
        ? fmt(str(strings, 'ACTIVITY_DUPLICATED_FROM', 'Duplicated from LOCALE #{0}'), d.source_epack_id)
        : '';
    case 'delete':
      return d.owner_user_id
        ? fmt(str(strings, 'ACTIVITY_DELETED_OWNER', 'Owner: #{0}'), d.owner_user_id)
        : '';
    case 'login':
      return [surfaceLabel(strings, d.surface), methodLabel(strings, d.method)].filter(Boolean).join(' / ');
    default:
      return '';
  }
}

/** "name (#id)" for a locale row; the id is what survives a rename or delete. */
export function localeLabel(row) {
  if (!row) {
    return '';
  }
  const name = row.entity_name || '';
  const id = row.entity_id ? '#' + row.entity_id : '';
  return name ? `${name} (${id})` : id;
}
